import { DomainPreferences } from '../../api/types';
import { RegularMetricId } from '../../api/types-graphql';
import {
  DashboardConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2Type,
} from '../../common/components/dashboards/types';
import { Languages } from '../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  DataTypes,
  EmployeeDataFields,
  FinancialYearSingleValueByQuartersTimeSelection,
  FinancialYearSingleValueByYearsTimeSelection,
  SegmentationByHierarchicalField,
  SegmentationByNonHierarchicalField,
  TimeSelectionType,
} from '../../types';
import { StoryboardDateUtils } from './utils';

const employmentTypeFilters = `EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = '契約社員' OR EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = '嘱託社員'
OR EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = '正社員' OR EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = '正社員（執行役員）'`;
const customFilter3Segmentation: SegmentationByNonHierarchicalField = {
  type: 'non-hierarchical',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.CUSTOM_FILTER_3,
  },
  filters: ['カード', 'デジタル', 'スタッフ'],
};
const positionSegmentation: SegmentationByHierarchicalField = {
  type: 'hierarchical',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.POSITION,
  },
  filters: [
    ['一般'],
    ['マネージャークラス'],
    ['部長クラス'],
    ['ディレクタークラス'],
    ['シニアディレクタークラス'],
  ],
};
const jobGradeSegmentation: SegmentationByNonHierarchicalField = {
  type: 'non-hierarchical',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
  },
};
const ageGroupSegmentation: SegmentationByNonHierarchicalField = {
  type: 'non-hierarchical',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.AGE_GROUP,
  },
};

const getFyQtdToCurrentMonthTimeSelection = (
  domainPreferences: DomainPreferences,
  { fiscalYear, fiscalQuarter }: StoryboardDateUtils
): FinancialYearSingleValueByQuartersTimeSelection => {
  return {
    type: TimeSelectionType.FinancialYearSingleValueByQuarters,
    input: {
      start: {
        year: fiscalYear(domainPreferences),
        quarterOfYear: fiscalQuarter(domainPreferences),
      },
      end: {
        year: fiscalYear(domainPreferences),
        quarterOfYear: fiscalQuarter(domainPreferences),
      },
    },
  };
};

const getFyYtdToCurrentMonthTimeSelection = (
  domainPreferences: DomainPreferences,
  { fiscalYear }: StoryboardDateUtils
): FinancialYearSingleValueByYearsTimeSelection => {
  return {
    type: TimeSelectionType.FinancialYearSingleValueByYears,
    input: {
      start: fiscalYear(domainPreferences),
      end: fiscalYear(domainPreferences),
    },
  };
};

const getCurrentMonthTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils
) => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const currentMonthTimeSelection: CalendarYearSingleValueByMonthsTimeSelection =
    {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: { start: nowEndOfMonth, end: nowEndOfMonth },
    };
  return currentMonthTimeSelection;
};

export const CreaturesDashboardChartConfig: DashboardConfig = {
  id: 'creatures_custom_db_1',
  title: {
    [Languages.EN]: 'Creatures Custom Dashboard',
    [Languages.JA]: '人材諸表®︎(月次報告)',
  },
  enabled: true,
  pages: [
    {
      pageId: '1',
      dashboardId: 'creatures_custom_db_1',
      pageNumber: 1,
      enabled: true,
      title: {
        [Languages.EN]: 'Page 1',
        [Languages.JA]: 'ページ１',
      },
      pageOptions: {
        displayOptions: {
          enabled: false,
        },
        timeSlider: {
          enabled: false,
        },
        filterTray: {
          enabled: false,
        },
        dataView: {
          enabled: false,
        },
        segmentationLevel1: {
          enabled: false,
        },
        segmentationLevel2: {
          enabled: false,
        },
        benchmark: {
          enabled: false,
        },
        forecast: {
          enabled: false,
        },
      },
      gridCols: 4,
      components: ({ storyboardDateUtils, domainPreferences }) => [
        {
          chartId: '1',
          title: {
            [Languages.EN]: 'Number of Individuals',
            [Languages.JA]: '従業員数',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.SINGLE_METRIC,
            section: {
              key: 'individuals-filtered',
              query: {
                metrics: [
                  {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0001_1IndividualsCount,
                  },
                ],
                timeSelection:
                  getCurrentMonthTimeSelection(storyboardDateUtils),
                filters: employmentTypeFilters,
                segmentations: [customFilter3Segmentation],
              },
              benchmarkConfigs: [
                {
                  benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
                  benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
                },
              ],
            },
          },
        },
        {
          chartId: '2',
          title: {
            [Languages.EN]: 'New hires (new graduates/mid-career)',
            [Languages.JA]: '入社者(新卒・中途)',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.DOUBLE_METRIC,
            sections: {
              leftSection: {
                key: 'joiners-qtd',
                title: {
                  [Languages.EN]: 'QTD',
                  [Languages.JA]: '四半期',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0006_1JoinersJoiners,
                    },
                  ],
                  timeSelection: getFyQtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
              rightSection: {
                key: 'joiners-ytd',
                title: {
                  // Not sure if YTD is the right translation
                  [Languages.EN]: 'YTD',
                  [Languages.JA]: '期初からの累計',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0006_1JoinersJoiners,
                    },
                  ],
                  timeSelection: getFyYtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
            },
          },
        },
        {
          chartId: '3',
          title: {
            [Languages.EN]: 'Resigners',
            [Languages.JA]: '離職者',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.DOUBLE_METRIC,
            sections: {
              leftSection: {
                key: 'resigners-qtd',
                title: {
                  [Languages.EN]: 'QTD',
                  [Languages.JA]: '四半期',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
                    },
                  ],
                  timeSelection: getFyQtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
              rightSection: {
                key: 'resigners-ytd',
                title: {
                  // Not sure if YTD is the right translation
                  [Languages.EN]: 'YTD',
                  [Languages.JA]: '期初からの累計',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
                    },
                  ],
                  timeSelection: getFyYtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
            },
          },
        },
        {
          chartId: '4',
          title: {
            [Languages.EN]: 'Position Distribution',
            [Languages.JA]: '役職分布',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          gridColStart: 4,
          gridColEnd: 5,
          options: {
            kpiType: KpiV2Type.SINGLE_METRIC,
            section: {
              key: 'individuals-position-distribution',
              query: {
                metrics: [
                  {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0001_1IndividualsCount,
                  },
                ],
                timeSelection:
                  getCurrentMonthTimeSelection(storyboardDateUtils),
                filters: employmentTypeFilters,
                segmentations: [positionSegmentation],
              },
              segmentationConfig: {
                showMainValue: false,
              },
            },
          },
        },
        {
          chartId: '5',
          title: {
            [Languages.EN]: 'Span of Control',
            [Languages.JA]: '管理職あたりの部下人数',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.SINGLE_METRIC,
            section: {
              key: 'span-of-control',
              query: {
                metrics: [
                  {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0024_2SpanControlAvg,
                  },
                ],
                timeSelection:
                  getCurrentMonthTimeSelection(storyboardDateUtils),
                filters: employmentTypeFilters,
                segmentations: [customFilter3Segmentation],
              },
            },
          },
        },
        {
          chartId: '6',
          title: {
            [Languages.EN]: 'Emps working >45 hrs',
            [Languages.JA]: '月45時間超過労働人数',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.DOUBLE_METRIC,
            sections: {
              leftSection: {
                key: 'emps-working-gt-45-hrs-qtd',
                title: {
                  [Languages.EN]: 'QTD',
                  [Languages.JA]: '四半期',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0214_2AvgEmp45HrsOTperMthHc,
                    },
                  ],
                  timeSelection: getFyQtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
              rightSection: {
                key: 'emps-working-gt-45-hrs-ytd',
                title: {
                  [Languages.EN]: 'YTD',
                  [Languages.JA]: '期初からの累計',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0214_2AvgEmp45HrsOTperMthHc,
                    },
                  ],
                  timeSelection: getFyYtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
            },
          },
        },
        {
          chartId: '7',
          title: {
            [Languages.EN]: 'Overtime work (average for employees)',
            [Languages.JA]: '時間外労働(従業員平均)',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.DOUBLE_METRIC,
            sections: {
              leftSection: {
                key: 'overtime-work-qtd',
                title: {
                  [Languages.EN]: 'QTD',
                  [Languages.JA]: '四半期',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0215_1MonthlyOtSumPerHc,
                    },
                  ],
                  timeSelection: getFyQtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
              rightSection: {
                key: 'overtime-work-ytd',
                title: {
                  [Languages.EN]: 'YTD',
                  [Languages.JA]: '期初からの累計',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0215_1MonthlyOtSumPerHc,
                    },
                  ],
                  timeSelection: getFyYtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
            },
          },
        },
        {
          chartId: '8',
          title: {
            [Languages.EN]: 'Persons on Leave',
            [Languages.JA]: '休職者',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'kpi-v2' },
          gridColSpan: 1,
          options: {
            kpiType: KpiV2Type.DOUBLE_METRIC,
            sections: {
              leftSection: {
                key: 'persons-on-leave-qtd',
                title: {
                  [Languages.EN]: 'QTD',
                  [Languages.JA]: '四半期',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0446_5SickLeaveDaysEmpCount,
                    },
                  ],
                  timeSelection: getFyQtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
              rightSection: {
                key: 'persons-on-leave-ytd',
                title: {
                  [Languages.EN]: 'YTD',
                  [Languages.JA]: '期初からの累計',
                },
                query: {
                  metrics: [
                    {
                      type: 'RegularMetricId',
                      value: RegularMetricId.Pm0446_5SickLeaveDaysEmpCount,
                    },
                  ],
                  timeSelection: getFyYtdToCurrentMonthTimeSelection(
                    domainPreferences,
                    storyboardDateUtils
                  ),
                  filters: employmentTypeFilters,
                  segmentations: [customFilter3Segmentation],
                },
              },
            },
          },
        },
      ],
    },
    {
      pageId: '2',
      dashboardId: 'creatures_custom_db_1',
      pageNumber: 2,
      enabled: true,
      title: {
        [Languages.EN]: 'Page 2',
        [Languages.JA]: 'ページ２',
      },
      pageOptions: {
        displayOptions: {
          enabled: false,
        },
        timeSlider: {
          enabled: false,
        },
        filterTray: {
          enabled: false,
        },
        dataView: {
          enabled: false,
        },
        segmentationLevel1: {
          enabled: false,
        },
        segmentationLevel2: {
          enabled: false,
        },
        benchmark: {
          enabled: false,
        },
        forecast: {
          enabled: false,
        },
      },
      gridCols: 1,
      gridRows: 1,
      components: ({ storyboardDateUtils, domainPreferences }) => [
        {
          chartId: '1',
          title: {
            [Languages.EN]: 'Number of Individuals',
            [Languages.JA]: '従業員数',
          },
          subtitle: {
            [Languages.EN]: '',
            [Languages.JA]: '',
          },
          chartTypeConfig: { chartType: 'table', timeframe: 'timePeriod' },
          gridRowSpan: 1,
          gridColSpan: 1,
          query: {
            title: {
              [Languages.EN]: 'Table',
              [Languages.JA]: 'Table',
            },
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: getCurrentMonthTimeSelection(storyboardDateUtils),
            filters: '',
            segmentations: [ageGroupSegmentation, jobGradeSegmentation],
          },
        },
      ],
    },
  ],
};
